import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Crosshair, Info } from 'lucide-react'
import { cn } from '@/lib/cn'
import { env } from '@/lib/env'
import { formatCurrency, pluralize } from '@/lib/format'
import { queryKeys } from '@/lib/query-client'
import type { AreaGroup, AreaPricingOverview, LatePricingInput, ServiceAreaBounds, SubArea } from '@/types/api'
import { useAdminMutation } from '@/hooks/useAdminMutation'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Spinner } from '@/components/ui/Spinner'
import { ErrorState } from '@/components/ui/States'
import { Badge } from '@/components/ui/StatusBadge'
import {
  createSubArea,
  deleteAreaGroup,
  deleteSubArea,
  fetchAreaPricing,
  setAreaPricingEnabled,
  setLatePricing,
  updateSubArea,
} from './api'
import { AreaMap, type MapArea, type MapMode, type TripPins } from './AreaMap'
import { describeLateWindow } from './describe'
import { GroupFormDialog } from './GroupFormDialog'
import { GroupsPanel } from './GroupsPanel'
import { LatePricingCard } from './LatePricingCard'
import { boundsOf, checkDraft, type Draft } from './polygonDraft'
import { PriceTable } from './PriceTable'
import { PricingModeCard } from './PricingModeCard'
import { SubAreaEditor } from './SubAreaEditor'
import { SubAreasPanel } from './SubAreasPanel'
import { toneClassForGroup } from './tones'
import { TripCheckPanel } from './TripCheckPanel'
import styles from './AreaPricingPage.module.css'

/** What the side panel is doing, which decides what a click on the map does. */
type Panel =
  | { kind: 'browse' }
  | { kind: 'edit'; subArea: SubArea | null; draft: Draft; name: string; groupId: number | null }
  | { kind: 'check' }

const NO_PINS: TripPins = { pickup: null, dropoff: null }

/**
 * Cairo divided into sub-areas, grouped and priced.
 *
 * The supervisor draws each sub-area on the map, puts it in a group, marks one
 * group primary and gives every other group a surcharge: what it costs to reach
 * the primary group from there. A Cairo booking then pays the surcharge of the
 * group its pickup is in plus the one its drop-off is in. The customer sees
 * only the result — each package's price with it already included.
 *
 * Cairo is priced in one of two modes, chosen at the top of the page: as one
 * area at one price — how it always was, and where it starts — or by sub-area.
 * Nothing drawn here reaches a customer until the second is chosen, so the
 * whole of Cairo can be drawn and checked first, and going back to one area
 * keeps it all for later.
 *
 * Late-night pricing sits on top, with a switch of its own: an end of a trip
 * that is late at night pays its group's late price. It waits for sub-area
 * pricing, and like it starts switched off.
 */
export function AreaPricingPage() {
  const [panel, setPanel] = useState<Panel>({ kind: 'browse' })
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [focus, setFocus] = useState<ServiceAreaBounds | null>(null)
  const [trip, setTrip] = useState<TripPins>(NO_PINS)
  const [groupDialog, setGroupDialog] = useState<{ open: boolean; group: AreaGroup | null }>({
    open: false,
    group: null,
  })
  const [deletingGroup, setDeletingGroup] = useState<AreaGroup | null>(null)
  const [deletingSubArea, setDeletingSubArea] = useState<SubArea | null>(null)
  /** The mode the supervisor picked and has not confirmed yet: true = by sub-area. */
  const [pendingMode, setPendingMode] = useState<boolean | null>(null)
  /** The late-night switch the supervisor picked and has not confirmed yet: true = late trips pay more. */
  const [pendingLate, setPendingLate] = useState<boolean | null>(null)

  useEffect(() => {
    document.title = `Cairo areas · ${env.appName} Operations`
  }, [])

  const overviewQuery = useQuery({
    queryKey: queryKeys.areaPricing.overview,
    queryFn: fetchAreaPricing,
  })
  const overview = overviewQuery.data

  // ------------------------------------------------------------ writes --

  const modeMutation = useAdminMutation({
    mutationFn: (enabled: boolean) => setAreaPricingEnabled(enabled),
    successTitle: 'Cairo pricing changed',
    successDescription: (saved: AreaPricingOverview) =>
      saved.enabled
        ? 'Cairo is priced by sub-area: every Cairo package price now includes the surcharges for where the trip starts and ends.'
        : 'Cairo is one area again: every trip costs its package price. The sub-areas and groups are kept.',
    errorTitle: 'Cairo pricing was not changed',
    invalidate: [queryKeys.areaPricing.all],
    onSuccess: () => setPendingMode(null),
  })

  const lateSwitchMutation = useAdminMutation({
    mutationFn: (input: LatePricingInput) => setLatePricing(input),
    successTitle: 'Late-night pricing changed',
    successDescription: (saved: AreaPricingOverview) =>
      !saved.latePricing.enabled
        ? 'Late Cairo trips cost the same as daytime ones again. The window and prices are kept.'
        : saved.latePricing.active
          ? `From the next quote, a Cairo trip end ${describeLateWindow(saved.latePricing)} pays its late price.`
          : 'Late-night pricing is on, and starts when Cairo is priced by sub-area.',
    errorTitle: 'Late-night pricing was not changed',
    invalidate: [queryKeys.areaPricing.all],
    onSuccess: () => setPendingLate(null),
  })

  const lateValuesMutation = useAdminMutation({
    mutationFn: (input: LatePricingInput) => setLatePricing(input),
    successTitle: 'Late window and prices saved',
    successDescription: (saved: AreaPricingOverview) =>
      saved.latePricing.active
        ? `From the next quote, a Cairo trip end ${describeLateWindow(saved.latePricing)} pays the new late price.`
        : 'Customers are not charged late prices until late-night pricing is on.',
    errorTitle: 'The late window and prices were not saved',
    invalidate: [queryKeys.areaPricing.all],
  })

  const saveSubAreaMutation = useAdminMutation({
    mutationFn: (editing: Extract<Panel, { kind: 'edit' }>) => {
      const input = { name: editing.name.trim(), groupId: editing.groupId, boundary: editing.draft.points }
      return editing.subArea ? updateSubArea(editing.subArea.id, input) : createSubArea(input)
    },
    successTitle: 'Sub-area saved',
    successDescription: (saved: SubArea) =>
      saved.groupName
        ? `${saved.name} is in ${saved.groupName}.`
        : `${saved.name} is in no group, so it adds nothing yet.`,
    errorTitle: 'The sub-area was not saved',
    invalidate: [queryKeys.areaPricing.all],
    onSuccess: (saved: SubArea) => {
      setPanel({ kind: 'browse' })
      setSelectedId(saved.id)
    },
  })

  const deleteSubAreaMutation = useAdminMutation({
    mutationFn: (subArea: SubArea) => deleteSubArea(subArea.id),
    successTitle: 'Sub-area deleted',
    successDescription: (_: void, subArea: SubArea) =>
      `Trips in ${subArea.name} are priced by whatever is left under them.`,
    errorTitle: 'The sub-area was not deleted',
    invalidate: [queryKeys.areaPricing.all],
    onSuccess: (_: void, subArea: SubArea) => {
      setDeletingSubArea(null)
      if (selectedId === subArea.id) setSelectedId(null)
      if (panel.kind === 'edit' && panel.subArea?.id === subArea.id) setPanel({ kind: 'browse' })
    },
  })

  const deleteGroupMutation = useAdminMutation({
    mutationFn: (group: AreaGroup) => deleteAreaGroup(group.id),
    successTitle: 'Group deleted',
    successDescription: (_: void, group: AreaGroup) =>
      group.subAreaCount > 0
        ? `Its ${group.subAreaCount} ${pluralize(group.subAreaCount, 'sub-area')} stay on the map in no group.`
        : undefined,
    errorTitle: 'The group was not deleted',
    invalidate: [queryKeys.areaPricing.all],
    onSuccess: () => setDeletingGroup(null),
  })

  // ------------------------------------------------------- the map's data --

  const groups = useMemo(() => overview?.groups ?? [], [overview])
  const editingId = panel.kind === 'edit' ? (panel.subArea?.id ?? null) : null

  const mapAreas = useMemo<MapArea[]>(
    () =>
      (overview?.subAreas ?? []).map((subArea) => {
        const group = groups.find((candidate) => candidate.id === subArea.groupId)
        return {
          id: subArea.id,
          name: subArea.name,
          detail: group
            ? `${group.name} · ${group.primary || group.surchargeMinor === 0 ? 'no surcharge' : `+${formatCurrency(group.surcharge)}`}`
            : 'No group',
          boundary: subArea.boundary,
          toneClass: toneClassForGroup(groups, subArea.groupId),
          selected: subArea.id === selectedId,
          hidden: subArea.id === editingId,
        }
      }),
    [overview, groups, selectedId, editingId],
  )

  const mode: MapMode = panel.kind === 'edit' ? 'draw' : panel.kind === 'check' ? 'check' : 'browse'
  const draft = panel.kind === 'edit' ? panel.draft : null
  const check = useMemo(
    () => (draft ? checkDraft(draft, overview?.serviceArea ?? null) : null),
    [draft, overview?.serviceArea],
  )

  // ------------------------------------------------------------- actions --

  const startDrawing = useCallback(() => {
    setSelectedId(null)
    setPanel({ kind: 'edit', subArea: null, draft: { points: [], closed: false }, name: '', groupId: null })
  }, [])

  const startEditing = useCallback((subArea: SubArea) => {
    setSelectedId(subArea.id)
    setFocus(boundsOf(subArea.boundary))
    setPanel({
      kind: 'edit',
      subArea,
      draft: { points: subArea.boundary, closed: true },
      name: subArea.name,
      groupId: subArea.groupId,
    })
  }, [])

  const selectSubArea = useCallback((subArea: SubArea) => {
    setSelectedId(subArea.id)
    setFocus(boundsOf(subArea.boundary))
  }, [])

  const updateEditor = useCallback((change: Partial<Extract<Panel, { kind: 'edit' }>>) => {
    setPanel((current) => (current.kind === 'edit' ? { ...current, ...change } : current))
  }, [])

  const busy =
    modeMutation.isPending ||
    lateSwitchMutation.isPending ||
    lateValuesMutation.isPending ||
    saveSubAreaMutation.isPending ||
    deleteSubAreaMutation.isPending ||
    deleteGroupMutation.isPending

  // ---------------------------------------------------------------- page --

  const currentPrimary = groups.find((group) => group.primary) ?? null

  return (
    <>
      <PageHeader
        title="Cairo areas"
        description="Divide Cairo into sub-areas and group them. A Cairo booking pays the surcharge of the group its pickup is in plus the one its drop-off is in; the primary group adds nothing. Customers only ever see the package price with it included. Trips to or from outside Cairo are not affected."
        meta={
          overview ? (
            <>
              <Badge tone={overview.enabled ? 'success' : 'neutral'} dot>
                {overview.enabled ? 'Priced by sub-area' : 'One area, one price'}
              </Badge>
              {overview.latePricing.active ? (
                <Badge tone="info" dot>
                  Late nights {overview.latePricing.start}–{overview.latePricing.end}
                </Badge>
              ) : null}
            </>
          ) : null
        }
      />

      {overviewQuery.isPending ? (
        <Card>
          <p className={styles.loading}>
            <Spinner size="sm" label={null} /> Loading Cairo&apos;s sub-areas…
          </p>
        </Card>
      ) : overviewQuery.isError || !overview ? (
        <Card>
          <ErrorState error={overviewQuery.error} onRetry={() => void overviewQuery.refetch()} />
        </Card>
      ) : (
        <>
          <PricingModeCard
            enabled={overview.enabled}
            updatedAt={overview.updatedAt}
            updatedByAdminName={overview.updatedByAdminName}
            disabled={busy}
            onChoose={(enabled) => {
              if (enabled !== overview.enabled) setPendingMode(enabled)
            }}
          />

          <LatePricingCard
            latePricing={overview.latePricing}
            areaPricingOn={overview.enabled}
            groups={groups}
            disabled={busy}
            saving={lateValuesMutation.isPending}
            onChoose={(enabled) => {
              if (enabled !== overview.latePricing.enabled) setPendingLate(enabled)
            }}
            onSave={(input) => lateValuesMutation.mutate(input)}
          />

          <Notices overview={overview} />

          <div className={styles.layout}>
            <div className={styles.main}>
              <Card>
                <div className={styles.toolbar}>
                  <div className={styles.modes}>
                    <Button
                      size="sm"
                      variant={panel.kind === 'check' ? 'primary' : 'secondary'}
                      iconLeft={<Crosshair aria-hidden="true" />}
                      onClick={() => setPanel(panel.kind === 'check' ? { kind: 'browse' } : { kind: 'check' })}
                      disabled={panel.kind === 'edit'}
                      aria-pressed={panel.kind === 'check'}
                    >
                      Check a trip
                    </Button>
                  </div>
                  <ul className={styles.legend} aria-label="Group colours">
                    {groups.map((group) => (
                      <li key={group.id} className={styles.legendItem}>
                        <span className={cn(styles.swatch, toneClassForGroup(groups, group.id))} aria-hidden="true" />
                        {group.name}
                      </li>
                    ))}
                    <li className={styles.legendItem}>
                      <span className={cn(styles.swatch, styles.toneNone)} aria-hidden="true" />
                      No group
                    </li>
                  </ul>
                </div>

                <AreaMap
                  bounds={overview.serviceArea}
                  areas={mapAreas}
                  mode={mode}
                  draft={draft}
                  trip={panel.kind === 'check' ? trip : NO_PINS}
                  focus={focus}
                  onSelectArea={(id) => {
                    const subArea = overview.subAreas.find((candidate) => candidate.id === id)
                    if (subArea) setSelectedId(subArea.id)
                  }}
                  onDraftChange={(next) => updateEditor({ draft: next })}
                  onTripChange={setTrip}
                />
                <p className={styles.hint}>
                  The dashed rectangle is Cairo: every sub-area has to fit inside it. Coloured shapes are sub-areas,
                  coloured by group.
                </p>
              </Card>

              <PriceTable groups={groups} pairs={overview.pairs} latePricing={overview.latePricing} />
            </div>

            <div className={styles.side}>
              {panel.kind === 'edit' && check ? (
                <SubAreaEditor
                  editingName={panel.subArea?.name ?? null}
                  draft={panel.draft}
                  check={check}
                  name={panel.name}
                  groupId={panel.groupId}
                  groups={groups}
                  saving={saveSubAreaMutation.isPending}
                  onDraftChange={(next) => updateEditor({ draft: next })}
                  onNameChange={(name) => updateEditor({ name })}
                  onGroupChange={(groupId) => updateEditor({ groupId })}
                  onSave={() => saveSubAreaMutation.mutate(panel)}
                  onCancel={() => setPanel({ kind: 'browse' })}
                  onDelete={panel.subArea ? () => setDeletingSubArea(panel.subArea) : null}
                />
              ) : null}

              {panel.kind === 'check' ? (
                <TripCheckPanel
                  trip={trip}
                  enabled={overview.enabled}
                  latePricing={overview.latePricing}
                  packages={overview.cairoPackages}
                  onClear={() => setTrip(NO_PINS)}
                  onDone={() => setPanel({ kind: 'browse' })}
                />
              ) : null}

              <GroupsPanel
                groups={groups}
                disabled={busy || panel.kind === 'edit'}
                onCreate={() => setGroupDialog({ open: true, group: null })}
                onEdit={(group) => setGroupDialog({ open: true, group })}
                onDelete={setDeletingGroup}
              />
              <SubAreasPanel
                subAreas={overview.subAreas}
                groups={groups}
                selectedId={selectedId}
                disabled={busy || panel.kind === 'edit'}
                onDraw={startDrawing}
                onSelect={selectSubArea}
                onEdit={startEditing}
                onDelete={setDeletingSubArea}
              />
            </div>
          </div>

          <GroupFormDialog
            open={groupDialog.open}
            group={groupDialog.group}
            currentPrimary={currentPrimary}
            onClose={() => setGroupDialog({ open: false, group: null })}
          />

          <ConfirmDialog
            open={pendingMode !== null}
            title={pendingMode ? 'Price Cairo by sub-area?' : 'Price Cairo as one area?'}
            message={
              pendingMode ? (
                <p>
                  From the next booking, every Cairo package price includes the surcharges for the groups the
                  trip starts and ends in, as listed under “What a trip adds”. Bookings already made do not
                  change.
                </p>
              ) : (
                <p>
                  From the next booking, every Cairo trip costs its package price again, wherever it starts and
                  ends — the sub-areas are ignored, including when a customer moves a pickup or drop-off.
                  Bookings already made keep what they were charged. The sub-areas, groups and surcharges stay
                  as they are, ready to use again.
                </p>
              )
            }
            confirmLabel={pendingMode ? 'Price by sub-area' : 'Use one area'}
            loading={modeMutation.isPending}
            onConfirm={() => pendingMode !== null && modeMutation.mutate(pendingMode)}
            onCancel={() => setPendingMode(null)}
          />

          <ConfirmDialog
            open={pendingLate !== null}
            title={pendingLate ? 'Charge more for late trips?' : 'Charge the same day and night?'}
            message={
              pendingLate ? (
                <>
                  <p>
                    From the next quote, an end of a Cairo trip that falls {describeLateWindow(overview.latePricing)}{' '}
                    pays its late price, as listed under “A late end costs”: the pickup when the trip starts then,
                    the drop-off when it ends then. Bookings already made do not change.
                  </p>
                  <p>
                    On the updated app, customers see one final price with it included before they pay. Older
                    versions of the app cannot include it, so late trips booked on them keep the daytime price.
                  </p>
                  {!overview.enabled ? (
                    <p>
                      Cairo is priced as one area at the moment, so nothing changes until it is priced by sub-area.
                    </p>
                  ) : null}
                </>
              ) : (
                <p>
                  From the next quote, a late Cairo trip costs what the same trip costs by day. Bookings already
                  made keep what they were charged. The window and the late prices stay as they are, ready to use
                  again.
                </p>
              )
            }
            confirmLabel={pendingLate ? 'Charge late trips more' : 'Same price day and night'}
            loading={lateSwitchMutation.isPending}
            onConfirm={() =>
              pendingLate !== null &&
              lateSwitchMutation.mutate({
                enabled: pendingLate,
                start: overview.latePricing.start,
                end: overview.latePricing.end,
                multiplier: overview.latePricing.multiplier,
                primaryFee: overview.latePricing.primaryFee,
              })
            }
            onCancel={() => setPendingLate(null)}
          />

          <ConfirmDialog
            open={deletingGroup !== null}
            title={deletingGroup ? `Delete ${deletingGroup.name}?` : 'Delete group?'}
            message={
              deletingGroup && deletingGroup.subAreaCount > 0 ? (
                <p>
                  Its {deletingGroup.subAreaCount} {pluralize(deletingGroup.subAreaCount, 'sub-area')} stay on the
                  map in no group, and stop adding a surcharge until you put them in another one.
                </p>
              ) : (
                <p>The group has no sub-areas, so no price changes.</p>
              )
            }
            confirmLabel="Delete group"
            tone="danger"
            loading={deleteGroupMutation.isPending}
            onConfirm={() => deletingGroup && deleteGroupMutation.mutate(deletingGroup)}
            onCancel={() => setDeletingGroup(null)}
          />

          <ConfirmDialog
            open={deletingSubArea !== null}
            title={deletingSubArea ? `Delete ${deletingSubArea.name}?` : 'Delete sub-area?'}
            message={
              <p>
                Trips that start or end there will be priced by whatever sub-area is left under them — or by none,
                which adds nothing. Bookings already made keep what they were charged.
              </p>
            }
            confirmLabel="Delete sub-area"
            tone="danger"
            loading={deleteSubAreaMutation.isPending}
            onConfirm={() => deletingSubArea && deleteSubAreaMutation.mutate(deletingSubArea)}
            onCancel={() => setDeletingSubArea(null)}
          />
        </>
      )}
    </>
  )
}

/** The checks worth making before pricing by sub-area, as short sentences above the map. */
function Notices({ overview }: { overview: AreaPricingOverview }) {
  const notices: { tone: 'warning' | 'info'; text: string }[] = []
  const ungrouped = overview.subAreas.filter((subArea) => subArea.groupId === null).length

  if (overview.groups.length === 0) {
    notices.push({
      tone: 'info',
      text: 'Start with the groups: create the primary group (for example Downtown), then one group for each surcharge.',
    })
  } else if (!overview.groups.some((group) => group.primary)) {
    notices.push({
      tone: 'warning',
      text: 'No group is marked primary. Every surcharge is meant to be the price of reaching the primary group.',
    })
  }
  if (ungrouped > 0) {
    notices.push({
      tone: 'warning',
      text: `${ungrouped} ${pluralize(ungrouped, 'sub-area is', 'sub-areas are')} in no group, so ${
        ungrouped === 1 ? 'it adds' : 'they add'
      } nothing to a trip.`,
    })
  }
  if (overview.enabled && overview.subAreas.length === 0) {
    notices.push({
      tone: 'warning',
      text: 'Cairo is set to be priced by sub-area, but no sub-areas are drawn — every trip still costs its package price.',
    })
  }

  if (notices.length === 0) return null

  return (
    <div className={styles.notices}>
      {notices.map((notice) => (
        <p key={notice.text} className={cn(styles.notice, notice.tone === 'info' && styles.noticeInfo)}>
          {notice.tone === 'info' ? <Info aria-hidden="true" /> : <AlertTriangle aria-hidden="true" />}
          {notice.text}
        </p>
      ))}
    </div>
  )
}
